import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Car, Calendar, User, Send, MessageSquare, CheckCircle2, Circle, MapPin, AlertTriangle, Plus, X, Wrench, FileText, Download, ShieldCheck, Sparkles, Layers, FileCheck, ExternalLink, Link2, Truck, Clock, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { clientsApi, smsApi, templatesApi, staffApi, contractorApi, CRM_BASE_URL, CONTRACTOR_APP_URL } from '../lib/api';
import { deliveryStages, contactStatuses, accessoryStatuses, checklistItems, registrationStatuses, handoverChecklistStatuses, tradeInStatuses, saleTypes, documentTypes, documentStatuses, activationStatuses, offerStatuses, yourWaySelections, siteLocations } from '../constants';
import { stageClass, formatDate, formatDateTime, renderTemplate, getReadinessDetails } from '../lib/utils';
import { toast } from 'sonner';

const SMS_SEGMENT_CHARS = 160;

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsBody, setSmsBody] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [comment, setComment] = useState('');
  const [accessoryName, setAccessoryName] = useState('');
  const [aftermarket, setAftermarket] = useState('');
  const [documentType, setDocumentType] = useState('Handover checklist');
  const [documentFileName, setDocumentFileName] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [offerMatches, setOfferMatches] = useState([]);
  const [activationReason, setActivationReason] = useState('');
  const [tradeInReason, setTradeInReason] = useState('');
  const [uploadLink, setUploadLink] = useState('');
  const [checks, setChecks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`checks_${id}`)) || {}; } catch { return {}; }
  });
  const [contractorJob, setContractorJob] = useState(null);
  const [contractorLoading, setContractorLoading] = useState(false);

  const reload = useCallback(async () => {
    const [c, m, t, matches, cJobs] = await Promise.all([
      clientsApi.get(id),
      smsApi.list({ client_id: id }),
      templatesApi.list(),
      clientsApi.offerMatches(id).catch(() => []),
      contractorApi.listJobsForClient(id).catch(() => []),
    ]);
    setClient(c);
    setMessages(m);
    setTemplates(t);
    setAgents([]);
    try { setAgents(await staffApi.list(c.site_location)); }
    catch { toast.error('Unable to load site staff'); }
    setOfferMatches(matches);
    setAftermarket(c.aftermarket_notes || '');
    
    // Find matching contractor job (by client_id or vin fallback)
    if (Array.isArray(cJobs) && cJobs.length > 0) {
      setContractorJob(cJobs[0]);
    } else if (c?.vin) {
      try {
        const vinJobs = await contractorApi.listJobsForClient(null, c.vin);
        setContractorJob(Array.isArray(vinJobs) && vinJobs.length > 0 ? vinJobs[0] : null);
      } catch {
        setContractorJob(null);
      }
    } else {
      setContractorJob(null);
    }

    setLoading(false);
  }, [id]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    let active = true;
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState !== 'visible') return;
      pending = true;
      try { const rows = await smsApi.list({ client_id: id }); if (active) setMessages(rows); }
      catch (error) { console.error('Message refresh failed', error); }
      finally { pending = false; }
    };
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener('focus', refresh);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [id]);

  const agentMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a.name])), [agents]);

  if (loading) return <p className="text-sm text-neutral-500">Loading…</p>;
  if (!client) return (
    <div className="text-center py-20">
      <p className="text-neutral-500">Client not found.</p>
      <Link to="/clients"><Button variant="outline" className="mt-4">Back to clients</Button></Link>
    </div>
  );

  const arrivedPending = client.arrived && client.stage !== 'Delivered';

  const patch = async (data) => {
    const updated = await clientsApi.update(id, data);
    setClient(updated);
    return updated;
  };

  const sendSms = async () => {
    if (sendingSms) return;
    if (!smsBody.trim()) { toast.error('Message cannot be empty'); return; }
    setSendingSms(true);
    try {
      const result = await smsApi.send({ client_id: id, body: smsBody });
      if (['failed', 'error', 'unconfigured', 'blocked'].includes(result.status)) throw new Error('SMS could not be sent');
      toast.success(result.status === 'simulated' ? 'SMS simulated' : 'SMS queued');
      setSmsBody('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'SMS failed');
    } finally { setSendingSms(false); }
  };

  const insertTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    const rendered = renderTemplate(tpl.body, {
      name: client.name.split(' ')[0],
      vehicle: client.vehicle,
      date: formatDate(client.delivery_date),
      agent: client.salesperson,
      rego: client.rego,
    });
    setSmsBody(rendered);
  };

  const saveComment = async () => {
    if (!comment.trim()) return;
    try {
      await clientsApi.addComment(id, comment);
      setComment('');
      reload();
      toast.success('Comment added');
    } catch (e) { toast.error('Failed'); }
  };

  const addAccessory = async () => {
    if (!accessoryName.trim()) return;
    try {
      await clientsApi.addAccessory(id, { name: accessoryName, status: 'Pending Order' });
      setAccessoryName('');
      reload();
    } catch (e) { toast.error('Failed'); }
  };

  const syncInventory = async () => {
    try {
      await clientsApi.syncInventory(id);
      reload();
      toast.success('Inventory synced from VY');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Inventory sync failed');
    }
  };

  const updateAccessory = async (aid, data) => {
    try { await clientsApi.updateAccessory(id, aid, data); reload(); }
    catch (e) { toast.error('Failed'); }
  };

  const removeAccessory = async (aid) => {
    try { await clientsApi.removeAccessory(id, aid); reload(); }
    catch (e) { toast.error('Failed'); }
  };

  const saveAftermarket = async () => {
    await patch({ aftermarket_notes: aftermarket });
    toast.success('Aftermarket notes saved');
  };

  const allocateToContractor = async () => {
    setContractorLoading(true);
    try {
      const newJob = await contractorApi.createFromClient(id);
      toast.success('Successfully allocated to Contractor Hub!');
      setContractorJob(newJob);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to allocate job to contractor');
    } finally {
      setContractorLoading(false);
    }
  };

  const syncContractorJob = async () => {
    if (!contractorJob) return;
    setContractorLoading(true);
    try {
      await contractorApi.syncJob(contractorJob.id);
      toast.success('Contractor job synced with Delivery Centre!');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to sync contractor job');
    } finally {
      setContractorLoading(false);
    }
  };

  const addDocument = async () => {
    try {
      await clientsApi.addDocument(id, {
        document_type: documentType,
        file_name: documentFileName || undefined,
        source: 'staff-upload',
        status: 'requested',
      });
      setDocumentFileName('');
      reload();
      toast.success('Document added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add document'); }
  };

  const uploadDocument = async () => {
    if (!documentFile) { toast.error('Choose a document first'); return; }
    try {
      await clientsApi.uploadDocument(id, documentFile, documentType);
      setDocumentFile(null);
      reload();
      toast.success('Document uploaded and attached');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const generatePack = async () => {
    try { await clientsApi.generatePack(id); reload(); toast.success('Document pack generated and filed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Pack generation failed'); }
  };

  const sendPack = async () => {
    try { await clientsApi.sendPack(id); reload(); toast.success('Document pack emailed to the customer'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Pack email failed'); }
  };

  const generateClaimPack = async () => {
    try { await clientsApi.generateClaimPack(id); reload(); toast.success('Offer claim pack generated and filed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Claim pack generation failed'); }
  };

  const createUploadLink = async () => {
    try {
      const result = await clientsApi.createUploadLink(id);
      setUploadLink(result.url);
      await navigator.clipboard?.writeText(result.url);
      toast.success('Customer upload link created and copied');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create upload link'); }
  };

  const updateDocument = async (documentId, data) => {
    try { await clientsApi.updateDocument(id, documentId, data); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update document'); }
  };

  const downloadDocument = async (document) => {
    try { await clientsApi.downloadDocument(id, document.id, document.file_name || 'document'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Download failed'); }
  };

  const updateTradeInStatus = async (status) => {
    try { await clientsApi.updateTradeIn(id, { trade_in_status: status, trade_in_manager_reason: tradeInReason || undefined }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Trade-in update failed'); }
  };

  const toggleCheck = (cid) => {
    const next = { ...checks, [cid]: !checks[cid] };
    setChecks(next);
    localStorage.setItem(`checks_${id}`, JSON.stringify(next));
  };

  const completedChecks = checklistItems.filter((c) => checks[c.id]).length;
  const tradeInDaysLeft = client.trade_in_valid_until ? Math.ceil((new Date(`${client.trade_in_valid_until}T23:59:59`) - new Date()) / 86400000) : null;
  const matchedOffers = offerMatches.filter((item) => item.eligible);
  const readiness = getReadinessDetails(client);

  return (
    <div className="space-y-6 max-w-6xl">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900">
        <ArrowLeft className="h-4 w-4"/> Back
      </button>

      {arrivedPending && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5"/>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">Vehicle has arrived but the delivery status hasn’t been updated.</p>
            <p className="text-xs text-amber-800 mt-0.5">Move to Pre-Delivery Inspection or Ready for Pickup once you’ve confirmed.</p>
          </div>
        </div>
      )}

      {/* Ready For Delivery Callout Banner */}
      {readiness.isReady && (
        <div className="rounded-xl border border-emerald-300 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 p-4 shadow-sm flex items-center justify-between flex-wrap gap-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-3.5">
            <div className="h-10 w-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md shrink-0">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold px-2.5 py-0.5 text-xs tracking-wider uppercase shadow-sm">
                  Ready For Delivery
                </Badge>
                <span className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-emerald-600" /> All 4 Operational Requirements Satisfied
                </span>
              </div>
              <p className="text-xs text-emerald-700 mt-0.5 font-medium">
                Payment Complete ✅ · Trade-in docs returned ✅ · PDI complete ✅ · Registration docs returned ✅
              </p>
            </div>
          </div>
          {client.stage !== 'Ready for Pickup' && client.stage !== 'Delivered' && (
            <Button
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold shadow-sm"
              onClick={() => patch({ stage: 'Ready for Pickup' })}
            >
              Update stage to Ready for Pickup
            </Button>
          )}
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">{client.name}</h1>
            {readiness.isReady && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 font-bold text-xs py-1 px-2.5 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Ready For Delivery
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-neutral-600">
            <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-neutral-400"/>{client.phone}</span>
            {client.email && <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-neutral-400"/>{client.email}</span>}
            <span className="flex items-center gap-1.5"><Car className="h-4 w-4 text-neutral-400"/>{client.vehicle} {client.rego ? `· ${client.rego}` : ''}</span>
            {client.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-neutral-400"/>{client.location}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {client.crm_customer_id && (
              <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 font-mono gap-1">
                <Link2 className="h-3 w-3 text-red-500" /> CRM: {client.crm_customer_id}
              </Badge>
            )}
            {client.crm_opportunity_id && (
              <Badge variant="outline" className="text-xs bg-neutral-100 text-neutral-700 border-neutral-200 font-mono">
                Deal: {client.crm_opportunity_id}
              </Badge>
            )}
            {client.vy_order_id && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 font-mono">
                VY Order: #{client.vy_order_id}
              </Badge>
            )}
            {client.salesperson && (
              <Badge variant="outline" className="text-xs bg-slate-100 text-slate-700 border-slate-200">
                Salesperson: {client.salesperson}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`${CRM_BASE_URL}/?search=${encodeURIComponent(client.phone || client.name || '')}&customer_id=${encodeURIComponent(client.crm_customer_id || '')}&opportunity_id=${encodeURIComponent(client.crm_opportunity_id || '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="bg-white border-neutral-300 hover:bg-neutral-50 text-neutral-800 text-xs font-semibold gap-1.5 shadow-sm">
              <ExternalLink className="h-3.5 w-3.5 text-[#E11B22]" /> Open in Sales CRM
            </Button>
          </a>
          <Link to={`/clients/${id}/inspection`}>
            <Button className="bg-[#E11B22] hover:bg-[#c4161c] text-white text-xs font-bold gap-1.5 shadow-sm">
              <FileCheck className="h-4 w-4" /> Digital Delivery Inspection
            </Button>
          </Link>
          <Select value={client.stage} onValueChange={(v) => patch({ stage: v })}>
            <SelectTrigger className="w-52 bg-white"><SelectValue/></SelectTrigger>
            <SelectContent>{deliveryStages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Badge className={`${stageClass(client.stage)} border-0 text-sm py-1.5 px-3`}>{client.stage}</Badge>
        </div>
      </div>

      {/* Operational Middle Layer Card */}
      <Card className={`border transition-all ${readiness.isReady ? 'border-emerald-300 bg-emerald-50/20' : 'border-neutral-200'}`}>
        <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-[#E11B22]" />
              <CardTitle className="text-base font-semibold">Operational Middle Layer · Delivery Requirements</CardTitle>
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">Track and verify the 4 operational requirements to mark vehicle Ready For Delivery</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-xs font-semibold border-0 ${
              readiness.isReady ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-100 text-neutral-700'
            }`}>
              {readiness.count}/4 Requirements Met
            </Badge>
            {!readiness.isReady && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 border-emerald-300 hover:bg-emerald-50 text-emerald-800 font-semibold"
                onClick={() => patch({
                  payment_complete: true,
                  trade_in_docs_complete: true,
                  pdi_complete: true,
                  registration_docs_complete: true,
                })}
              >
                Mark all 4 complete
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* 1. Payment */}
            <div className={`p-3.5 rounded-lg border transition-all ${
              readiness.payment ? 'bg-emerald-50/70 border-emerald-200' : 'bg-white border-neutral-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-neutral-900 flex items-center gap-1.5">
                  {readiness.payment ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-neutral-400" />}
                  Payment Complete
                </span>
                <Switch
                  checked={Boolean(client.payment_complete)}
                  onCheckedChange={(v) => patch({ payment_complete: v })}
                />
              </div>
              <p className="text-[11px] text-neutral-500 leading-tight">
                {client.payment_complete ? 'Payment confirmed & cleared' : 'Awaiting payment confirmation'}
              </p>
            </div>

            {/* 2. Trade-in Docs */}
            <div className={`p-3.5 rounded-lg border transition-all ${
              readiness.tradeInDocs ? 'bg-emerald-50/70 border-emerald-200' : 'bg-white border-neutral-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-neutral-900 flex items-center gap-1.5">
                  {readiness.tradeInDocs ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-neutral-400" />}
                  Trade-in Docs Returned
                </span>
                <Switch
                  checked={Boolean(client.trade_in_docs_complete || (!readiness.hasTradeIn && readiness.tradeInDocs))}
                  disabled={!readiness.hasTradeIn}
                  onCheckedChange={(v) => patch({ trade_in_docs_complete: v })}
                />
              </div>
              <p className="text-[11px] text-neutral-500 leading-tight">
                {!readiness.hasTradeIn
                  ? 'No trade-in attached (Auto-satisfied)'
                  : client.trade_in_docs_complete
                  ? 'All trade-in docs returned'
                  : 'Trade-in docs outstanding'}
              </p>
            </div>

            {/* 3. Pre-Delivery Inspection */}
            <div className={`p-3.5 rounded-lg border transition-all ${
              readiness.pdi ? 'bg-emerald-50/70 border-emerald-200' : 'bg-white border-neutral-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-neutral-900 flex items-center gap-1.5">
                  {readiness.pdi ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-neutral-400" />}
                  PDI Complete
                </span>
                <Switch
                  checked={Boolean(client.pdi_complete || client.stage === 'Ready for Pickup' || client.stage === 'Delivered')}
                  onCheckedChange={(v) => patch({ pdi_complete: v })}
                />
              </div>
              <p className="text-[11px] text-neutral-500 leading-tight">
                {readiness.pdi ? 'Workshop inspection complete' : 'Pre-delivery inspection pending'}
              </p>
              <Link
                to={`/clients/${id}/inspection`}
                className="text-[11px] font-bold text-[#E11B22] hover:underline mt-1.5 flex items-center gap-1"
              >
                <FileCheck className="h-3 w-3" />
                {readiness.pdi ? 'View Signed Inspection Form →' : 'Launch Digital Inspection Form →'}
              </Link>
            </div>

            {/* 4. Registration Docs */}
            <div className={`p-3.5 rounded-lg border transition-all ${
              readiness.registrationDocs ? 'bg-emerald-50/70 border-emerald-200' : 'bg-white border-neutral-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-neutral-900 flex items-center gap-1.5">
                  {readiness.registrationDocs ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-neutral-400" />}
                  Registration Docs Returned
                </span>
                <Switch
                  checked={Boolean(client.registration_docs_complete || client.registration_status === 'Complete')}
                  onCheckedChange={(v) => patch({ registration_docs_complete: v })}
                />
              </div>
              <p className="text-[11px] text-neutral-500 leading-tight">
                {readiness.registrationDocs ? 'All registration docs returned' : 'Registration docs awaiting return'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status row */}
      <Card className="border-neutral-200">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Box label="Contact status">
            <Select value={client.contact_status} onValueChange={(v) => patch({ contact_status: v })}>
              <SelectTrigger className="h-9"><SelectValue/></SelectTrigger>
              <SelectContent>{contactStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {client.last_contacted_at && <p className="text-[10px] text-neutral-500 mt-1">Last: {formatDateTime(client.last_contacted_at)}</p>}
          </Box>
          <Box label="Assigned agent">
            <Select value={client.assigned_agent_id || 'unassigned'} onValueChange={(v) => patch({ assigned_agent_id: v === 'unassigned' ? '' : v })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Unassigned"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Box>
          <Box label="Vehicle arrived">
            <div className="flex items-center gap-2 h-9">
              <Switch checked={client.arrived} onCheckedChange={(v) => patch({ arrived: v })}/>
              <span className="text-sm text-neutral-700">{client.arrived ? 'Yes' : 'No'}</span>
            </div>
            {client.arrived_at && <p className="text-[10px] text-neutral-500 mt-1">Since: {formatDateTime(client.arrived_at)}</p>}
          </Box>
          <Box label="Delivery date">
            <Input type="date" defaultValue={client.delivery_date || ''} onBlur={(e) => e.target.value !== (client.delivery_date || '') && patch({ delivery_date: e.target.value })}/>
          </Box>
        </CardContent>
      </Card>

      <Card className="border-neutral-200">
        <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Registration & compliance</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Box label="PO / INV">
            <Input defaultValue={client.po_number || ''} onBlur={(e) => e.target.value !== (client.po_number || '') && patch({ po_number: e.target.value })} />
          </Box>
          <Box label="Payment method">
            <Select value={client.payment_method || 'none'} onValueChange={(v) => patch({ payment_method: v === 'none' ? null : v })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Not set" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not set</SelectItem>
                <SelectItem value="Cash">Cash / Transfer</SelectItem>
                <SelectItem value="Finance">Finance</SelectItem>
                <SelectItem value="Novated lease">Novated lease</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Box>
          <Box label="Sale type">
            <Select value={client.sale_type || 'Retail'} onValueChange={(v) => patch({ sale_type: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{saleTypes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Box>
          <Box label="Fleet / FMO company">
            <Input defaultValue={client.fleet_company || ''} onBlur={(e) => e.target.value !== (client.fleet_company || '') && patch({ fleet_company: e.target.value })} />
          </Box>
          <Box label="Fleet reference">
            <Input defaultValue={client.fleet_reference || ''} onBlur={(e) => e.target.value !== (client.fleet_reference || '') && patch({ fleet_reference: e.target.value })} />
          </Box>
          <Box label="Lease consultant">
            <Input defaultValue={client.lease_consultant || ''} onBlur={(e) => e.target.value !== (client.lease_consultant || '') && patch({ lease_consultant: e.target.value })} />
          </Box>
          <Box label="Registered operator">
            <Select value={client.registered_operator_type || 'Individual'} onValueChange={(v) => patch({ registered_operator_type: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Individual">Individual</SelectItem><SelectItem value="Company">Company</SelectItem></SelectContent>
            </Select>
          </Box>
          <Box label="Registration status">
            <Select value={client.registration_status || 'Awaiting registration documents'} onValueChange={(v) => patch({ registration_status: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{registrationStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Box>
          <Box label="Trade-in flag">
            <div className="flex items-center gap-2 h-9">
              <Switch checked={Boolean(client.trade_in_flag)} onCheckedChange={(v) => patch({ trade_in_flag: v })} />
              <span className="text-sm text-neutral-700">{client.trade_in_flag ? 'Yes' : 'No'}</span>
            </div>
          </Box>
          <Box label="Trade-in attached">
            <div className="flex items-center gap-2 h-9">
              <Switch checked={Boolean(client.trade_in_attached)} onCheckedChange={(v) => patch({ trade_in_attached: v })} />
              <span className="text-sm text-neutral-700">{client.trade_in_attached ? 'Yes' : 'No'}</span>
            </div>
          </Box>
          <Box label="Trade-in sale date">
            <Input type="date" defaultValue={client.trade_in_sale_date || ''} onBlur={(e) => e.target.value !== (client.trade_in_sale_date || '') && patch({ trade_in_sale_date: e.target.value })} />
          </Box>
          <Box label="Trade-in valid until">
            <Input type="date" defaultValue={client.trade_in_valid_until || ''} onBlur={(e) => e.target.value !== (client.trade_in_valid_until || '') && patch({ trade_in_valid_until: e.target.value })} />
            {client.trade_in_attached && client.trade_in_valid_until && <p className={`text-[10px] mt-1 ${tradeInDaysLeft <= 0 ? 'text-red-600' : tradeInDaysLeft <= 10 ? 'text-amber-700' : 'text-neutral-500'}`}>{tradeInDaysLeft <= 0 ? 'Trade-in expired' : `Trade-in valid for ${tradeInDaysLeft} day${tradeInDaysLeft === 1 ? '' : 's'}`}</p>}
          </Box>
          <Box label="Trade-in status">
            <Select value={client.trade_in_status || 'Pending'} onValueChange={updateTradeInStatus}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{tradeInStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Box>
          <Box label="Trade-in manager reason">
            <Input value={tradeInReason} onChange={(e) => setTradeInReason(e.target.value)} placeholder="Required for expired settlement" />
          </Box>
          <Box label="Handover checklist">
            <Select value={client.handover_checklist_status || 'Not issued'} onValueChange={(v) => patch({ handover_checklist_status: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{handoverChecklistStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Link
              to={`/clients/${id}/inspection`}
              className="text-[10px] font-semibold text-[#E11B22] hover:underline mt-1 inline-block"
            >
              Open Digital Handover Checklist →
            </Link>
          </Box>
          <Box label="Activation status">
            <Select value={client.activation_status || 'Blocked'} onValueChange={async (v) => {
              try { await clientsApi.updateActivation(id, { activation_status: v, activation_override_reason: activationReason || undefined }); reload(); }
              catch (e) { toast.error(e?.response?.data?.detail || 'Activation update failed'); }
            }}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{activationStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Box>
          <Box label="Activation override reason">
            <Input value={activationReason} onChange={(e) => setActivationReason(e.target.value)} placeholder="Manager reason if checklist evidence is unavailable" />
          </Box>
          <Box label="Offer status">
            <Select value={client.offer_status || 'Eligible'} onValueChange={async (v) => {
              try { await clientsApi.updateOfferStatus(id, { offer_status: v }); reload(); }
              catch (e) { toast.error(e?.response?.data?.detail || 'Offer update failed'); }
            }}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{offerStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Box>
          <Box label="Linked offer">
            <Select value={client.linked_offer_ids?.[0] || 'none'} onValueChange={(v) => patch({ linked_offer_ids: v === 'none' ? [] : [v] })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Select offer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {matchedOffers.map(({ offer }) => <SelectItem key={offer.id} value={offer.id}>{offer.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {matchedOffers.length === 0 && <p className="text-[10px] text-amber-700 mt-1">No currently eligible catalogue offer.</p>}
          </Box>
          <Box label="Your Way selection">
            <Select value={client.your_way_selection || 'none'} onValueChange={(v) => patch({ your_way_selection: v === 'none' ? null : v })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Not selected" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Not selected</SelectItem>{yourWaySelections.map((choice) => <SelectItem key={choice} value={choice}>{choice}</SelectItem>)}</SelectContent>
            </Select>
          </Box>
        </CardContent>
      </Card>

      <Card className="border-neutral-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between"><CardTitle className="text-base font-semibold flex items-center gap-2"><FileText className="h-4 w-4"/> Registration documents</CardTitle><div className="flex gap-2 flex-wrap"><Button onClick={generatePack} variant="outline" size="sm"><FileText className="h-4 w-4 mr-1"/>Generate pack</Button><Button onClick={generateClaimPack} variant="outline" size="sm"><FileText className="h-4 w-4 mr-1"/>Claim pack</Button><Button onClick={sendPack} variant="outline" size="sm"><Send className="h-4 w-4 mr-1"/>Email pack</Button><Button onClick={createUploadLink} variant="outline" size="sm"><FileText className="h-4 w-4 mr-1"/>Customer upload link</Button></div></CardHeader>
        <CardContent className="space-y-3">
          {uploadLink && <p className="break-all rounded-md bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-600">Upload link: {uploadLink}</p>}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{documentTypes.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Returned file name (optional)" value={documentFileName} onChange={(e) => setDocumentFileName(e.target.value)} />
            <Button onClick={addDocument} variant="outline"><Plus className="h-4 w-4 mr-1"/>Add document</Button>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input type="file" className="max-w-md" onChange={(e) => setDocumentFile(e.target.files?.[0] || null)} />
            <Button onClick={uploadDocument} variant="outline"><FileText className="h-4 w-4 mr-1"/>Upload and attach</Button>
          </div>
          {(client.documents || []).length === 0 ? <p className="text-sm text-neutral-500">No registration documents tracked yet.</p> : (
            <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
              {client.documents.map((document) => (
                <div key={document.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center px-3 py-2.5">
                  <div className="text-sm"><p className="font-medium">{document.document_type}</p><p className="text-xs text-neutral-500">{document.file_name || 'No file returned'}</p></div>
                  <Select value={document.status} onValueChange={(v) => updateDocument(document.id, { status: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{documentStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="h-8 text-xs" placeholder="Notes" defaultValue={document.notes || ''} onBlur={(e) => e.target.value !== (document.notes || '') && updateDocument(document.id, { notes: e.target.value })} />
                  {document.storage_path && <Button variant="ghost" size="icon" title="Download document" onClick={() => downloadDocument(document)}><Download className="h-4 w-4"/></Button>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-neutral-200">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold flex items-center gap-2"><Send className="h-4 w-4 text-[#E11B22]"/> Send SMS</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Select onValueChange={insertTemplate}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Insert template…"/></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea rows={4} placeholder={`Message to ${client.phone}…`} value={smsBody} onChange={(e) => setSmsBody(e.target.value)} />
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>{smsBody.length} chars · {Math.ceil(smsBody.length / SMS_SEGMENT_CHARS) || 1} SMS segment</span>
                <Button onClick={sendSms} disabled={sendingSms || !smsBody.trim()} className="bg-[#E11B22] hover:bg-[#B81319] text-white">Send SMS</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4"/> Conversation</CardTitle></CardHeader>
            <CardContent>
              {messages.length === 0 ? <p className="text-sm text-neutral-500">No messages yet.</p> : (
                <div className="space-y-3">
                  {[...messages].reverse().map((m) => (
                    <div key={m.id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.direction === 'outbound' ? 'bg-[#E11B22] text-white' : 'bg-neutral-100 text-neutral-900'}`}>
                        <p>{m.body}</p>
                        <p className={`text-[10px] mt-1 ${m.direction === 'outbound' ? 'text-red-100' : 'text-neutral-500'}`}>{formatDateTime(m.sent_at)} · {m.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Wrench className="h-4 w-4"/> Accessories &amp; aftermarket</CardTitle>
              <Button variant="outline" size="sm" onClick={syncInventory} className="text-xs h-8 gap-1">
                Sync from VY
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input placeholder="Add accessory (e.g. Floor Mats Moulded)" value={accessoryName} onChange={(e) => setAccessoryName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addAccessory()}/>
                <Button onClick={addAccessory} variant="outline"><Plus className="h-4 w-4 mr-1"/>Add</Button>
              </div>
              {(client.accessories || []).length === 0 ? (
                <p className="text-sm text-neutral-500 italic py-2">No accessories tracked yet.</p>
              ) : (
                <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
                  {client.accessories.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 px-3 py-2.5">
                      <p className="flex-1 text-sm font-medium">{a.name}</p>
                      <Select value={a.status} onValueChange={(v) => updateAccessory(a.id, { status: v })}>
                        <SelectTrigger className="h-8 w-36 text-xs"><SelectValue/></SelectTrigger>
                        <SelectContent>{accessoryStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <button onClick={() => removeAccessory(a.id)} className="text-neutral-400 hover:text-red-500"><X className="h-4 w-4"/></button>
                    </div>
                  ))}
                </div>
              )}
              <Separator/>
              <Label className="text-xs font-semibold text-neutral-700 mb-1.5 block">Aftermarket notes</Label>
              <Textarea rows={2} placeholder="Anything still to be ordered or fitted…" value={aftermarket} onChange={(e) => setAftermarket(e.target.value)} onBlur={saveAftermarket}/>
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Truck className="h-4 w-4 text-[#E11B22]" /> Contractor Job &amp; Prep Hub
              </CardTitle>
              {contractorJob ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={syncContractorJob}
                    disabled={contractorLoading}
                    className="text-xs h-8 gap-1.5"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${contractorLoading ? 'animate-spin' : ''}`} />
                    Sync Status
                  </Button>
                  <a
                    href={`${CONTRACTOR_APP_URL || 'https://byd-contractor-app.vercel.app'}/jobs/${contractorJob.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#E11B22] font-semibold hover:underline"
                  >
                    Open in Hub <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-4">
              {contractorJob ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Status:</span>
                        <Badge variant="outline" className={`font-semibold capitalize text-xs ${
                          contractorJob.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : contractorJob.status === 'blocked'
                            ? 'bg-red-50 text-red-700 border-red-300'
                            : 'bg-blue-50 text-blue-700 border-blue-300'
                        }`}>
                          {contractorJob.status?.replace('_', ' ') || 'New'}
                        </Badge>
                        <Badge variant="outline" className="text-[11px] capitalize">
                          Priority: {contractorJob.priority || 'Normal'}
                        </Badge>
                        {contractorJob.is_urgent && (
                          <Badge className="bg-red-600 text-white text-[11px]">URGENT</Badge>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500">
                        Assigned Contractor: <span className="font-semibold text-neutral-800">{contractorJob.assigned_contractor_name || 'Unassigned'}</span>
                      </p>
                    </div>
                    <div className="text-right text-xs text-neutral-500">
                      <div className="flex items-center justify-end gap-1 font-medium text-neutral-700">
                        <Clock className="h-3.5 w-3.5 text-neutral-400" />
                        <span>{Math.round(contractorJob.total_time_minutes || 0)} mins logged</span>
                      </div>
                      <p className="text-[11px] text-neutral-400 mt-0.5">
                        Location: {contractorJob.current_location_name || contractorJob.site_location || 'Dealership'}
                      </p>
                    </div>
                  </div>

                  {contractorJob.flagged_issues && contractorJob.flagged_issues.length > 0 && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs space-y-1">
                      <div className="flex items-center gap-1.5 font-semibold text-red-800">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span>Flagged Issues / Blockers:</span>
                      </div>
                      {contractorJob.flagged_issues.map((issue, idx) => (
                        <p key={idx} className="text-red-700 pl-5">
                          • {issue.issue_type}: {issue.notes} ({issue.status})
                        </p>
                      ))}
                    </div>
                  )}

                  {((contractorJob.tasks && contractorJob.tasks.length > 0) || (contractorJob.checklist && contractorJob.checklist.length > 0)) && (() => {
                    const jobTasks = contractorJob.tasks || contractorJob.checklist || [];
                    const completedCount = jobTasks.filter((t) => t.completed).length;
                    const percent = jobTasks.length > 0 ? Math.round((completedCount / jobTasks.length) * 100) : 0;
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-semibold text-neutral-700">
                            Prep Checklist ({completedCount} / {jobTasks.length} done)
                          </span>
                          <span className="text-neutral-500 font-medium">
                            {percent}%
                          </span>
                        </div>
                        <Progress value={percent} className="h-2" />
                        <div className="divide-y divide-neutral-100 rounded-md border border-neutral-200 text-xs">
                          {jobTasks.map((task, i) => (
                            <div key={task.id || i} className="flex items-center justify-between px-3 py-2">
                              <div className="flex items-center gap-2">
                                {task.completed ? (
                                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                                ) : (
                                  <Circle className="h-4 w-4 text-neutral-300 shrink-0" />
                                )}
                                <span className={task.completed ? 'line-through text-neutral-400' : 'text-neutral-800 font-medium'}>
                                  {task.label || task.title}
                                </span>
                              </div>
                              {task.completed_by_name && (
                                <span className="text-[11px] text-neutral-400">by {task.completed_by_name}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="flex justify-between items-center text-[11px] text-neutral-400 pt-1">
                    <span>Job ID: {contractorJob.id}</span>
                    <span>Last synced: {formatDateTime(contractorJob.last_synced_at || contractorJob.updated_at)}</span>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50/50 p-6 text-center">
                  <Truck className="h-8 w-8 text-neutral-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-neutral-800">No Contractor Job Allocated</p>
                  <p className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">
                    Dispatch this vehicle to the BYD Contractor Hub for window tinting, accessories fitting, detailing, and pre-delivery inspection.
                  </p>
                  <Button
                    onClick={allocateToContractor}
                    disabled={contractorLoading}
                    className="mt-4 bg-[#E11B22] hover:bg-[#B81319] text-white text-xs h-9 gap-1.5"
                  >
                    <Plus className="h-4 w-4" />
                    {contractorLoading ? 'Allocating...' : 'Allocate to Contractor Hub'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Comments &amp; handover</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea rows={2} placeholder="Add an internal comment for the team…" value={comment} onChange={(e) => setComment(e.target.value)}/>
              <div className="flex justify-end"><Button onClick={saveComment} variant="outline">Add comment</Button></div>
              <Separator/>
              {(client.comments || []).length === 0 ? <p className="text-sm text-neutral-500">No comments yet.</p> : (
                <div className="space-y-3">
                  {[...(client.comments || [])].reverse().map((c) => (
                    <div key={c.id} className="flex gap-3">
                      <div className="h-8 w-8 rounded-full bg-neutral-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">{(c.author_name || '').split(' ').map((p) => p[0]).join('').slice(0, 2)}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-xs text-neutral-500"><span className="font-semibold text-neutral-900">{c.author_name}</span> · {formatDateTime(c.created_at)}</div>
                        <p className="text-sm mt-0.5 whitespace-pre-line">{c.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-neutral-200">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Vehicle &amp; Address</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row icon={Car} label="Vehicle" value={client.vehicle}/>
              <Row label="Rego" value={client.rego}/>
              <Row label="VIN" value={client.vin}/>
              <Row label="Deal type" value={client.deal_type}/>
              <Row icon={MapPin} label="Street Address" value={client.address || client.location}/>
              <Row icon={Calendar} label="Delivery" value={formatDate(client.delivery_date)}/>
              {client.vy_order_id && <Row label="VY order" value={client.vy_order_id}/>}
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold flex items-center gap-2"><MapPin className="h-4 w-4"/> Site &amp; Staff Assignment</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div>
                <Label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Dealership Site Location</Label>
                <Select value={client.site_location || 'BYD Caroline Springs'} onValueChange={(v) => patch({ site_location: v })}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue/></SelectTrigger>
                  <SelectContent>{siteLocations.filter((s) => s !== 'All Sites').map((site) => <SelectItem key={site} value={site}>{site}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Primary Salesperson</Label>
                <Input value={client.salesperson || ''} placeholder="Primary sales rep" onChange={(e) => patch({ salesperson: e.target.value })} className="h-8 text-xs bg-white"/>
              </div>

              <div>
                <Label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Secondary Salesperson</Label>
                <Input value={client.secondary_salesperson || ''} placeholder="Secondary sales rep (if 2 sales reps)" onChange={(e) => patch({ secondary_salesperson: e.target.value })} className="h-8 text-xs bg-white"/>
              </div>

              <div>
                <Label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Delivery Follow-Up Consultant</Label>
                <Input value={client.delivery_consultant || ''} placeholder="Consultant for delivery follow-up" onChange={(e) => patch({ delivery_consultant: e.target.value })} className="h-8 text-xs bg-white"/>
              </div>

              <div>
                <Label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Handover Specialist</Label>
                <Input value={client.handover_specialist || ''} placeholder="Staff performing physical handover" onChange={(e) => patch({ handover_specialist: e.target.value })} className="h-8 text-xs bg-white"/>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">Handover checklist</CardTitle>
              <span className="text-xs font-medium text-neutral-500">{completedChecks}/{checklistItems.length}</span>
            </CardHeader>
            <CardContent className="space-y-2">
              {checklistItems.map((item) => (
                <div key={item.id} className="space-y-1 py-0.5">
                  <button onClick={() => toggleCheck(item.id)} className="flex items-start gap-2.5 w-full px-2 py-1.5 rounded-md hover:bg-neutral-50 text-left">
                    {checks[item.id] ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5"/> : <Circle className="h-4 w-4 text-neutral-300 shrink-0 mt-0.5"/>}
                    <span className={`text-sm ${checks[item.id] ? 'text-neutral-400 line-through' : 'text-neutral-800'}`}>{item.label}</span>
                  </button>
                  {item.hasInput && (
                    <div className="pl-8 pr-2 pb-1">
                      <Input
                        placeholder="Enter Business Client ID #"
                        value={client.business_client_id || ''}
                        onChange={(e) => patch({ business_client_id: e.target.value })}
                        className="h-8 text-xs bg-white border-neutral-200"
                      />
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-neutral-500 font-semibold flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5"/>}{label}</span>
      <span className="font-medium text-neutral-900 text-right truncate">{value}</span>
    </div>
  );
}

function Box({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
